import { useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, EyeOff, Folder, FolderOpen, FolderPlus, Pencil } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi, useMe } from '@blimo/api-client/react';
import { formatNumber, hasPermission } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Dialog,
  DialogContent,
  EmptyState,
  ErrorState,
  Skeleton,
  toast,
} from '@blimo/ui';
import { FolderForm } from './folder-form.jsx';
import { DocumentUpload } from './document-upload.jsx';
import { FolderDocuments } from './document-table.jsx';

/** Wer sieht veröffentlichte Dokumente im Portal – je Ordner-Bereich (Backend: `DocumentAccess`). */
const SCOPE_HINTS = {
  property: 'Veröffentlichte Dokumente in sichtbaren Ordnern sehen im Portal alle aktuellen Eigentümer dieser Liegenschaft.',
  unit: 'Veröffentlichte Dokumente in sichtbaren Ordnern sehen im Portal nur die aktuellen Eigentümer dieser Einheit („persönliche Dateien“).',
  global: 'Veröffentlichte Dokumente in sichtbaren Ordnern sehen im Portal alle Benutzer (z. B. Formulare, Merkblätter).',
  tenancy:
    'Ordner eines Mietverhältnisses sind intern. Nur wenn ein Ordner im Portal sichtbar ist, sehen veröffentlichte Dokumente Portal-Benutzer mit Recht „Mietverhältnisse ansehen“ für diese Einheit (z. B. Mandanten) – Eigentümer nicht.',
};

/**
 * Dokumentenverwaltung für einen Ordner-Bereich: Liegenschaft, Einheit, Mietverhältnis oder global (Formulare & Downloads).
 * Ordner anlegen/umbenennen/sortieren/im Portal ein- und ausblenden, Upload (mehrere Dateien, Fortschritt),
 * Titel/Beschreibung, Veröffentlichen (optional Eigentümer benachrichtigen), Löschen, Download und Vorschau.
 * Aktionen erscheinen nur mit `documents.upload` / `documents.publish` / `documents.delete` – geprüft wird serverseitig.
 * @param {{ ownerType: 'property'|'unit'|'tenancy'|'global', ownerId?: string|number, title?: string }} props
 */
export function DocumentManager({ ownerType, ownerId, title = 'Dokumente' }) {
  const api = useApi();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const params = ownerType === 'global' ? { owner_type: 'global' } : { owner_type: ownerType, owner_id: String(ownerId) };
  const query = useQuery(adminQueries.documentFolders(api, params));
  const [selectedId, setSelectedId] = useState(/** @type {number|null} */ (null));
  const [folderDialog, setFolderDialog] = useState(/** @type {{ folder?: any } | null} */ (null));
  const [busy, setBusy] = useState(false);
  const listLabelId = useId();

  const can = {
    upload: hasPermission(me, 'documents.upload'),
    publish: hasPermission(me, 'documents.publish'),
    delete: hasPermission(me, 'documents.delete'),
  };
  const invalidate = () => queryClient.invalidateQueries({ queryKey: keys.admin.resource('document-folders') });

  if (query.error) return <ErrorState error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) {
    return (
      <div className="grid gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]" aria-busy="true">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  const folders = query.data ?? [];
  const folder = folders.find((f) => f.id === selectedId) ?? folders[0] ?? null;
  const index = folder ? folders.indexOf(folder) : -1;

  /** @param {any} target @param {Record<string, any>} payload @param {string} success */
  async function updateFolder(target, payload, success) {
    setBusy(true);
    try {
      await api.admin.documentFolders.update(target.id, payload);
      toast.success(success);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Ordner konnte nicht geändert werden.'));
    } finally {
      setBusy(false);
      invalidate();
    }
  }

  /** Reihenfolge ändern: alle Ordner fortlaufend nummerieren (Abstand 10), nur geänderte senden. @param {-1|1} direction */
  async function move(direction) {
    const order = [...folders];
    const [moved] = order.splice(index, 1);
    order.splice(index + direction, 0, moved);
    setBusy(true);
    try {
      for (const [i, f] of order.entries()) {
        if (f.sort !== i * 10) await api.admin.documentFolders.update(f.id, { sort: i * 10 });
      }
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Reihenfolge konnte nicht gespeichert werden.'));
    } finally {
      setBusy(false);
      invalidate();
    }
  }

  const createButton = can.upload && (
    <Button size="sm" variant="outline" onClick={() => setFolderDialog({})}>
      <FolderPlus aria-hidden="true" /> Ordner anlegen
    </Button>
  );

  return (
    <section className="flex flex-col gap-3" aria-label={title}>
      {folders.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="Noch keine Ordner"
          description={can.upload ? `Legen Sie einen Ordner an, um Dokumente hochzuladen. ${SCOPE_HINTS[ownerType]}` : SCOPE_HINTS[ownerType]}
          action={createButton || undefined}
        />
      ) : (
        <div className="grid items-start gap-3 lg:grid-cols-[16rem_minmax(0,1fr)]">
          <Card className="gap-0 py-0">
            <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
              <h3 id={listLabelId} className="text-sm font-semibold">
                Ordner
              </h3>
              {can.upload && (
                <Button size="icon-sm" variant="ghost" onClick={() => setFolderDialog({})} aria-label="Ordner anlegen" title="Ordner anlegen">
                  <FolderPlus aria-hidden="true" />
                </Button>
              )}
            </div>
            <ul aria-labelledby={listLabelId} className="flex flex-col gap-0.5 p-1.5">
              {folders.map((f) => {
                const active = folder?.id === f.id;
                return (
                  <li key={f.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(f.id)}
                      aria-current={active ? 'true' : undefined}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent',
                        active && 'bg-accent font-medium text-accent-foreground',
                      )}
                    >
                      {active ? (
                        <FolderOpen className="size-4 shrink-0 text-primary" aria-hidden="true" />
                      ) : (
                        <Folder className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                      )}
                      <span className="min-w-0 flex-1 truncate">{f.name}</span>
                      {!f.visible_in_portal && <EyeOff className="size-3.5 shrink-0 text-muted-foreground" aria-label="im Portal ausgeblendet" role="img" />}
                      <span className="numeric text-xs text-muted-foreground" aria-label={`${f.documents_count ?? 0} Dokumente`}>
                        {formatNumber(f.documents_count ?? 0)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </Card>

          {folder && (
            <Card role="region" aria-label={`Ordner ${folder.name}`} className="min-w-0">
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
                <div className="flex min-w-0 flex-col gap-1">
                  <CardTitle as="h3" className="flex flex-wrap items-center gap-2">
                    <span className="truncate">{folder.name}</span>
                    {folder.visible_in_portal ? <Badge variant="success">Im Portal sichtbar</Badge> : <Badge variant="neutral">Im Portal ausgeblendet</Badge>}
                  </CardTitle>
                  <CardDescription>{SCOPE_HINTS[ownerType]}</CardDescription>
                </div>
                {can.upload && (
                  <div className="flex flex-wrap items-center gap-1">
                    <label className="mr-1 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        role="switch"
                        className="size-4 accent-primary"
                        checked={Boolean(folder.visible_in_portal)}
                        disabled={busy}
                        onChange={(e) =>
                          updateFolder(
                            folder,
                            { visible_in_portal: e.target.checked },
                            e.target.checked ? `„${folder.name}“ ist im Portal sichtbar` : `„${folder.name}“ ist im Portal ausgeblendet`,
                          )
                        }
                      />
                      Im Portal sichtbar
                    </label>
                    <Button size="sm" variant="ghost" onClick={() => setFolderDialog({ folder })} disabled={busy}>
                      <Pencil aria-hidden="true" /> Umbenennen
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => move(-1)}
                      disabled={busy || index <= 0}
                      aria-label="Ordner nach oben verschieben"
                      title="Nach oben"
                    >
                      <ArrowUp aria-hidden="true" />
                    </Button>
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => move(1)}
                      disabled={busy || index >= folders.length - 1}
                      aria-label="Ordner nach unten verschieben"
                      title="Nach unten"
                    >
                      <ArrowDown aria-hidden="true" />
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                {can.upload && <DocumentUpload key={folder.id} folder={folder} ownerType={ownerType} canPublish={can.publish} onUploaded={invalidate} />}
                <FolderDocuments folder={folder} ownerType={ownerType} can={can} onChanged={invalidate} />
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {can.upload && (
        <Dialog open={Boolean(folderDialog)} onOpenChange={(open) => !open && setFolderDialog(null)}>
          <DialogContent>
            {folderDialog && (
              <FolderForm
                folder={folderDialog.folder}
                defaultVisible={ownerType !== 'tenancy'}
                onDone={() => setFolderDialog(null)}
                onSave={async (values) => {
                  if (folderDialog.folder) {
                    await api.admin.documentFolders.update(folderDialog.folder.id, { name: values.name });
                    toast.success('Ordner umbenannt');
                  } else {
                    const created = await api.admin.documentFolders.create({
                      owner_type: ownerType,
                      owner_id: ownerType === 'global' ? null : Number(ownerId),
                      name: values.name,
                      visible_in_portal: values.visible_in_portal,
                    });
                    if (created?.id) setSelectedId(created.id);
                    toast.success('Ordner angelegt');
                  }
                  await invalidate();
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
}
